import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { db } from './firebase-config';
import {
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { getActiveCompanyId } from './core/company/companyConfig';
import {
  resolveDriversCollectionPath,
  resolveFleetExpenseCategoriesCollectionPath,
  resolveFleetExpensesCollectionPath,
  resolveTransportsCollectionPath,
} from './core/company/companyPaths';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { getTransportVisualConfig, getTransportLabel } from './modules/logistics/components/mapUtils';
import { resolveTransportIdentity } from './modules/logistics/components/transportIdentity';
import { FLEET_EXPENSES_TUTORIAL_STEPS } from './modules/fleet/tutorials/fleetExpensesTutorial';
import {
  DEFAULT_FLEET_EXPENSE_CATEGORIES,
  ensureDefaultFleetExpenseCategories,
  sortFleetCategories,
} from './modules/fleet/config/fleetExpenseCategoryCatalog';

const DEFAULT_COMPANY_ID = 'default';
const DEFAULT_COMPANY_NAME = 'Empresa Base';
const FLEET_TUTORIAL_STORAGE_KEY = 'gcentra_tutorial_fleet_expenses_seen';
const FLEET_TUTORIAL_PANEL_WIDTH = 288;
const FLEET_TUTORIAL_PANEL_HEIGHT = 420;


const Icon = ({ path, className = 'w-6 h-6' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const formatCurrency = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '0,00';
  return value.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getLocalDateAsString = (date) => {
  const d = date ? new Date(date) : new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


const buildTransportDisplayName = (transport = {}) => {
  return (
    transport?.name ||
    transport?.transportModel ||
    transport?.vehicleLabel ||
    transport?.patent ||
    'Unidad sin nombre'
  );
};

const buildTransportMeta = (transport = {}) => {
  return [
    getTransportLabel(transport?.transportType || 'camioneta'),
    transport?.transportModel || transport?.vehicleLabel || '',
    transport?.patent || ''
  ]
    .filter(Boolean)
    .join(' · ');
};

const buildDriverDisplayName = (driver = {}) => {
  const firstName = String(driver?.firstName || driver?.driverName || driver?.name || '').trim();
  const lastName = String(driver?.lastName || driver?.driverLastName || '').trim();
  return [firstName, lastName].filter(Boolean).join(' ').trim() || 'Chofer sin nombre';
};

const buildDriverMeta = (driver = {}) => {
  return [driver?.dni || '', driver?.phone || ''].filter(Boolean).join(' · ');
};

const SectionBadge = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
  };

  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
};

const PanelHeader = ({ eyebrow, title, description, extra = null }) => (
  <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
    <div className="max-w-2xl">
      <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">{eyebrow}</p>
      <h3 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">{title}</h3>
      {description ? <p className="mt-2 text-sm leading-relaxed text-slate-500">{description}</p> : null}
    </div>
    {extra ? <div className="flex flex-wrap items-center gap-2">{extra}</div> : null}
  </div>
);

const StatCard = ({ title, value, helper, accent = 'slate', iconPath = '' }) => {
  const accents = {
    slate: 'from-slate-600/10 to-slate-400/5 border-slate-200',
    blue: 'from-blue-600/10 to-cyan-500/5 border-blue-100',
    emerald: 'from-emerald-600/10 to-green-500/5 border-emerald-100',
    violet: 'from-violet-600/10 to-fuchsia-500/5 border-violet-100',
  };

  return (
    <div className={`rounded-[26px] border bg-gradient-to-br p-4 shadow-sm ${accents[accent] || accents.slate}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{title}</p>
          <p className="mt-2 text-[1.7rem] font-bold tracking-[-0.03em] leading-none text-slate-900">{value}</p>
          {helper ? <p className="mt-1.5 text-[13px] leading-snug text-slate-500">{helper}</p> : null}
        </div>
        {iconPath ? (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-600 shadow-sm">
            <Icon path={iconPath} className="h-4.5 w-4.5" />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const FieldLabel = ({ children }) => (
  <label className="mb-2 block text-sm font-medium text-slate-700">{children}</label>
);

const EmptyState = ({ title, description, action = null }) => (
  <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-6 py-14 text-center">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
      <Icon path="M19.5 14.25v-2.625a3.375 3.375 0 00-1.028-2.426L13.5 4.5a3.375 3.375 0 00-2.386-.99H6.75A2.25 2.25 0 004.5 5.76v12.48a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25z" className="h-7 w-7 text-slate-500" />
    </div>
    <h4 className="mt-5 text-lg font-semibold text-slate-800">{title}</h4>
    <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">{description}</p>
    {action ? <div className="mt-6">{action}</div> : null}
  </div>
);

const FleetExpensesApp = ({
  onBackToMenu,
  showMessage,
  companyId: companyIdProp,
  companyName: companyNameProp,
  onGiaContextChange,
  onStartTutorial = null,
  tutorialSeen: tutorialSeenProp = false,
  tutorialActive = false,
  tutorialFocusTarget = null,
}) => {
  const resolvedCompanyId = companyIdProp || getActiveCompanyId?.() || DEFAULT_COMPANY_ID;
  const resolvedCompanyName = companyNameProp || DEFAULT_COMPANY_NAME;

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transports, setTransports] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    date: getLocalDateAsString(),
    type: '',
    transportId: '',
    amount: '',
    description: '',
  });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#3b82f6');
  const [filter, setFilter] = useState({ month: '', type: '', transportId: '' });
  const [tutorialSeen, setTutorialSeen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(FLEET_TUTORIAL_STORAGE_KEY) === 'true';
  });
  const [tutorialRun, setTutorialRun] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialShortcutOpen, setTutorialShortcutOpen] = useState(false);
  const [tutorialShortcutDock, setTutorialShortcutDock] = useState('top-right');
  const [tutorialSessionKey, setTutorialSessionKey] = useState(0);
  const effectiveTutorialSeen = onStartTutorial ? tutorialSeenProp : tutorialSeen;
  const effectiveTutorialRunning = onStartTutorial ? tutorialActive : tutorialRun;

  useEffect(() => {
    if (!tutorialActive || !tutorialFocusTarget || typeof window === 'undefined') return undefined;
    const timeoutId = window.setTimeout(() => {
      const targetNode = document.querySelector(tutorialFocusTarget);
      targetNode?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 180);
    return () => window.clearTimeout(timeoutId);
  }, [tutorialActive, tutorialFocusTarget]);
  const tutorialShortcutPanelRef = useRef(null);
  const tutorialUtteranceRef = useRef(null);
  const tutorialSpeechTokenRef = useRef(0);
  const [giaSection, setGiaSection] = useState('Resumen de flota');

  useEffect(() => {
    if (typeof onGiaContextChange !== 'function') return undefined;
    onGiaContextChange({
      subsectionLabel: giaSection,
      focusLabel: '',
    });
    return () => {
      onGiaContextChange({
        subsectionLabel: '',
        focusLabel: '',
      });
    };
  }, [giaSection, onGiaContextChange]);

  const categoriesPath = useMemo(
    () => resolveFleetExpenseCategoriesCollectionPath(resolvedCompanyId),
    [resolvedCompanyId]
  );

  const expensesPath = useMemo(
    () => resolveFleetExpensesCollectionPath(resolvedCompanyId),
    [resolvedCompanyId]
  );

  const transportsPath = useMemo(
    () => resolveTransportsCollectionPath(resolvedCompanyId),
    [resolvedCompanyId]
  );

  const driversPath = useMemo(
    () => resolveDriversCollectionPath(resolvedCompanyId),
    [resolvedCompanyId]
  );

  useEffect(() => {
    ensureDefaultFleetExpenseCategories({
      db,
      companyId: resolvedCompanyId,
      companyName: resolvedCompanyName,
    }).catch((error) => {
      console.error('Error asegurando categorías base de flota:', error);
    });
  }, [resolvedCompanyId, resolvedCompanyName]);

  useEffect(() => {
    const categoriesRef = collection(db, categoriesPath);

    const unsubscribe = onSnapshot(
      categoriesRef,
      (snapshot) => {
        const fetchedCategories = sortFleetCategories(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setCategories(fetchedCategories);

        setFormData((prev) => {
          const currentTypeStillExists = fetchedCategories.some((cat) => cat.id === prev.type);
          if (currentTypeStillExists) return prev;

          return {
            ...prev,
            type: fetchedCategories[0]?.id || '',
          };
        });
      },
      (error) => {
        console.error('Error cargando categorías de flota:', error);
        setCategories([]);
        showMessage?.('No se pudieron cargar las categorías de flota.', 'error');
      }
    );

    return () => unsubscribe();
  }, [categoriesPath, showMessage]);


  useEffect(() => {
    const transportsRef = collection(db, transportsPath);

    const unsubscribe = onSnapshot(
      transportsRef,
      (snapshot) => {
        const fetchedTransports = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => buildTransportDisplayName(a).localeCompare(buildTransportDisplayName(b), 'es', { sensitivity: 'base' }));

        setTransports(fetchedTransports);

        setFormData((prev) => {
          const currentTransportStillExists = fetchedTransports.some((transport) => transport.id === prev.transportId);
          if (currentTransportStillExists) return prev;

          return {
            ...prev,
            transportId: fetchedTransports[0]?.id || '',
          };
        });
      },
      (error) => {
        console.error('Error cargando unidades de flota:', error);
        setTransports([]);
        showMessage?.('No se pudieron cargar las unidades disponibles.', 'error');
      }
    );

    return () => unsubscribe();
  }, [showMessage, transportsPath]);

  useEffect(() => {
    const driversRef = collection(db, driversPath);

    const unsubscribe = onSnapshot(
      driversRef,
      (snapshot) => {
        const fetchedDrivers = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => buildDriverDisplayName(a).localeCompare(buildDriverDisplayName(b), 'es', { sensitivity: 'base' }));

        setDrivers(fetchedDrivers);

        setFormData((prev) => {
          if (!prev.driverId) return prev;
          const currentDriverStillExists = fetchedDrivers.some((driver) => driver.id === prev.driverId);
          return currentDriverStillExists ? prev : { ...prev, driverId: '' };
        });
      },
      (error) => {
        console.error('Error cargando choferes de flota:', error);
        setDrivers([]);
      }
    );

    return () => unsubscribe();
  }, [driversPath]);

  useEffect(() => {
    setLoading(true);

    const expensesRef = collection(db, expensesPath);
    const expensesQuery = query(expensesRef, orderBy('date', 'desc'));

    const unsubscribe = onSnapshot(
      expensesQuery,
      (snapshot) => {
        setExpenses(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
      },
      (error) => {
        console.error('Error cargando gastos de flota:', error);
        setExpenses([]);
        setLoading(false);
        showMessage?.('No se pudieron cargar los gastos de flota.', 'error');
      }
    );

    return () => unsubscribe();
  }, [expensesPath, showMessage]);


  const selectedTransport = useMemo(
    () => transports.find((transport) => transport.id === formData.transportId) || null,
    [formData.transportId, transports]
  );

  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === formData.type) || null,
    [categories, formData.type]
  );

  const selectedDriver = useMemo(
    () => drivers.find((driver) => driver.id === formData.driverId) || null,
    [drivers, formData.driverId]
  );

  useEffect(() => {
    if (!selectedCategory?.impactDriver) {
      setFormData((prev) => (prev.driverId ? { ...prev, driverId: '' } : prev));
      return;
    }

    const suggestedDriverId = selectedTransport?.currentDriverId || selectedTransport?.driverId || '';
    if (!suggestedDriverId) return;

    setFormData((prev) => {
      if (prev.driverId) return prev;
      return { ...prev, driverId: suggestedDriverId };
    });
  }, [selectedCategory, selectedTransport]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.date || !formData.type || !formData.transportId) {
      showMessage('Fecha, categoría, unidad y monto son obligatorios.', 'error');
      return;
    }

    if (!selectedTransport) {
      showMessage('Elegí una unidad válida para registrar el gasto.', 'error');
      return;
    }

    if (selectedCategory?.requiresDriverSelection && !selectedDriver) {
      showMessage('Elegí el chofer involucrado para registrar esta categoría.', 'error');
      return;
    }

    const transportIdentity = resolveTransportIdentity(selectedTransport);
    const transportVisual = getTransportVisualConfig(selectedTransport.transportType || 'camioneta');
    const transportName = buildTransportDisplayName(selectedTransport);
    const transportMeta = buildTransportMeta(selectedTransport);

    try {
      await addDoc(collection(db, expensesPath), {
        ...formData,
        amount: parseFloat(formData.amount),
        companyId: resolvedCompanyId,
        companyName: resolvedCompanyName,
        transportId: selectedTransport.id,
        transportName,
        transportType: selectedTransport.transportType || '',
        transportLabel: getTransportLabel(selectedTransport.transportType || 'camioneta'),
        transportModel: selectedTransport.transportModel || selectedTransport.vehicleLabel || '',
        transportPatent: selectedTransport.patent || '',
        transportMeta,
        identityColor: selectedTransport.identityColor || transportIdentity.identityColor,
        identitySoftColor: selectedTransport.identitySoftColor || transportIdentity.identitySoftColor,
        transportIconPath: transportVisual.path,
        categoryName: selectedCategory?.name || '',
        categoryColor: selectedCategory?.color || '',
        categoryScopeLabel: selectedCategory?.scopeLabel || '',
        impactTransport: selectedCategory?.impactTransport !== false,
        impactDriver: Boolean(selectedCategory?.impactDriver),
        isSystemCategory: Boolean(selectedCategory?.isSystem),
        driverId: selectedDriver?.id || '',
        driverName: selectedDriver ? buildDriverDisplayName(selectedDriver) : '',
        driverMeta: selectedDriver ? buildDriverMeta(selectedDriver) : '',
        driverPhone: selectedDriver?.phone || '',
        driverPhotoURL: selectedDriver?.photoURL || '',
        createdAt: serverTimestamp(),
      });

      showMessage(`Gasto registrado en ${transportName}.`, 'success');
      setFormData({
        date: getLocalDateAsString(),
        type: categories[0]?.id || '',
        transportId: selectedTransport.id,
        driverId: '',
        amount: '',
        description: '',
      });
    } catch (error) {
      showMessage('Error al añadir el gasto.', 'error');
      console.error('Error adding expense: ', error);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este gasto?')) {
      try {
        await deleteDoc(doc(db, expensesPath, id));
        showMessage('Gasto eliminado.', 'success');
      } catch (error) {
        showMessage('Error al eliminar el gasto.', 'error');
        console.error('Error deleting expense: ', error);
      }
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!newCategoryName.trim()) {
      showMessage('El nombre de la categoría no puede estar vacío.', 'error');
      return;
    }

    try {
      await addDoc(collection(db, categoriesPath), {
        name: newCategoryName.trim(),
        color: newCategoryColor,
        companyId: resolvedCompanyId,
        companyName: resolvedCompanyName,
        order: 900 + categories.filter((item) => !item.isFixed).length,
        isFixed: false,
        isSystem: false,
        impactTransport: true,
        impactDriver: false,
        requiresDriverSelection: false,
        scopeLabel: 'Transporte',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      showMessage('Categoría añadida con éxito.', 'success');
      setNewCategoryName('');
      setNewCategoryColor('#3b82f6');
    } catch (error) {
      showMessage('Error al añadir la categoría.', 'error');
      console.error('Error adding category: ', error);
    }
  };

  const handleDeleteCategory = async (id) => {
    const categoryToDelete = categories.find((item) => item.id === id);

    if (categoryToDelete?.isFixed || categoryToDelete?.isSystem) {
      showMessage('Las categorías base de la empresa vienen protegidas y no se pueden eliminar.', 'error');
      return;
    }

    if (window.confirm('¿Seguro que quieres eliminar esta categoría? Esto no eliminará los gastos ya registrados.')) {
      try {
        await deleteDoc(doc(db, categoriesPath, id));
        showMessage('Categoría eliminada.', 'success');
      } catch (error) {
        showMessage('Error al eliminar la categoría.', 'error');
        console.error('Error deleting category: ', error);
      }
    }
  };

  const filteredExpenses = useMemo(() => {
    return expenses.filter((exp) => {
      const expenseMonth = exp.date.substring(0, 7);
      const monthMatch = !filter.month || expenseMonth === filter.month;
      const typeMatch = !filter.type || exp.type === filter.type;
      const transportMatch = !filter.transportId || exp.transportId === filter.transportId;
      return monthMatch && typeMatch && transportMatch;
    });
  }, [expenses, filter]);

  const statsData = useMemo(() => {
    const byCategory = {};

    filteredExpenses.forEach((exp) => {
      const category = categories.find((c) => c.id === exp.type);
      const categoryName = category ? category.name : 'Desconocida';

      if (!byCategory[categoryName]) {
        byCategory[categoryName] = {
          total: 0,
          color: category ? category.color : '#808080',
        };
      }

      byCategory[categoryName].total += exp.amount;
    });

    const categoryChartData = Object.entries(byCategory).map(([name, data]) => ({
      name,
      value: data.total,
      color: data.color,
    }));

    return { categoryChartData };
  }, [filteredExpenses, categories]);

  const availableMonths = useMemo(() => {
    const months = new Set(expenses.map((exp) => exp.date.substring(0, 7)));
    return Array.from(months).sort().reverse();
  }, [expenses]);

  const currentMonth = getLocalDateAsString().slice(0, 7);

  const summary = useMemo(() => {
    const totalFiltered = filteredExpenses.reduce((acc, item) => acc + (Number(item.amount) || 0), 0);
    const totalCurrentMonth = expenses
      .filter((item) => String(item.date || '').slice(0, 7) === currentMonth)
      .reduce((acc, item) => acc + (Number(item.amount) || 0), 0);

    const categoryTotals = filteredExpenses.reduce((acc, item) => {
      const key = item.type || 'unknown';
      acc[key] = (acc[key] || 0) + (Number(item.amount) || 0);
      return acc;
    }, {});

    const [topCategoryId = ''] = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1])[0] || [];
    const topCategory = categories.find((item) => item.id === topCategoryId)?.name || 'Sin datos';
    const averageExpense = filteredExpenses.length ? totalFiltered / filteredExpenses.length : 0;

    return {
      totalFiltered,
      totalCurrentMonth,
      expenseCount: filteredExpenses.length,
      topCategory,
      averageExpense,
    };
  }, [categories, currentMonth, expenses, filteredExpenses]);

  const tutorialSteps = useMemo(() => FLEET_EXPENSES_TUTORIAL_STEPS, []);
  const currentTutorialTarget = tutorialRun ? tutorialSteps[tutorialStepIndex]?.target || null : null;

  useEffect(() => {
    if (!tutorialRun || typeof window === 'undefined') return undefined;

    const panelNode = tutorialShortcutPanelRef.current;
    if (!panelNode) return undefined;

    const updateDock = () => {
      const targetNode = currentTutorialTarget ? document.querySelector(currentTutorialTarget) : null;
      if (!targetNode) {
        setTutorialShortcutDock('top-right');
        return;
      }

      const rect = targetNode.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const panelWidth = Math.min(FLEET_TUTORIAL_PANEL_WIDTH, Math.max(260, panelNode.offsetWidth || FLEET_TUTORIAL_PANEL_WIDTH));
      const panelHeight = Math.min(FLEET_TUTORIAL_PANEL_HEIGHT, Math.max(300, panelNode.offsetHeight || FLEET_TUTORIAL_PANEL_HEIGHT));
      const margin = 20;

      const candidates = {
        'top-right': { left: viewportWidth - panelWidth - margin, top: 96 },
        'top-left': { left: margin, top: 96 },
        'bottom-right': { left: viewportWidth - panelWidth - margin, top: viewportHeight - panelHeight - margin },
        'bottom-left': { left: margin, top: viewportHeight - panelHeight - margin },
      };

      const getOverlapArea = (candidate) => {
        const left = Math.max(candidate.left, rect.left);
        const top = Math.max(candidate.top, rect.top);
        const right = Math.min(candidate.left + panelWidth, rect.right);
        const bottom = Math.min(candidate.top + panelHeight, rect.bottom);
        const width = Math.max(0, right - left);
        const height = Math.max(0, bottom - top);
        return width * height;
      };

      const bestCandidate = Object.entries(candidates)
        .map(([candidate, pos]) => ({ candidate, area: getOverlapArea(pos) }))
        .sort((a, b) => a.area - b.area)[0];

      setTutorialShortcutDock(bestCandidate?.candidate || 'top-right');
    };

    updateDock();
    window.addEventListener('resize', updateDock);
    window.addEventListener('scroll', updateDock, true);
    return () => {
      window.removeEventListener('resize', updateDock);
      window.removeEventListener('scroll', updateDock, true);
    };
  }, [currentTutorialTarget, tutorialRun, tutorialStepIndex]);

  const finishTutorialRun = useCallback(() => {
    tutorialSpeechTokenRef.current += 1;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    tutorialUtteranceRef.current = null;
    setTutorialRun(false);
    setTutorialStepIndex(0);
    setTutorialShortcutOpen(false);
    setTutorialSessionKey((current) => current + 1);
  }, []);

  const speakTutorialStep = useCallback((step) => {
    const text = String(step?.narration || step?.content || '').trim();
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;

    tutorialSpeechTokenRef.current += 1;
    const token = tutorialSpeechTokenRef.current;
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'es-AR';
    utterance.rate = 0.97;
    utterance.pitch = 1.04;
    utterance.volume = 1;

    const voices = window.speechSynthesis.getVoices?.() || [];
    const preferredVoice =
      voices.find((voice) => /es(-|_)(ar|es|mx|us)/i.test(voice.lang) && /female|helena|laura|sofia|paulina|monica|maria|sabina/i.test(voice.name))
      || voices.find((voice) => /es(-|_)(ar|es|mx|us)/i.test(voice.lang))
      || null;

    if (preferredVoice) utterance.voice = preferredVoice;
    tutorialUtteranceRef.current = utterance;

    window.setTimeout(() => {
      if (tutorialSpeechTokenRef.current !== token) return;
      window.speechSynthesis.speak(utterance);
    }, 120);
  }, []);

  const startTutorial = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(FLEET_TUTORIAL_STORAGE_KEY, 'true');
    }
    setTutorialSeen(true);
    setTutorialSessionKey((current) => current + 1);
    setTutorialRun(true);
    setTutorialStepIndex(0);
    setTutorialShortcutOpen(true);
    speakTutorialStep(tutorialSteps[0]);
  }, []);

  const scrollToTutorialTarget = useCallback((step) => {
    if (typeof window === 'undefined' || !step?.target) return false;

    const targetNode = document.querySelector(step.target);
    if (!targetNode) return false;

    try {
      targetNode.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      return true;
    } catch (error) {
      targetNode.scrollIntoView();
      return true;
    }
  }, []);

  const jumpToTutorialStep = useCallback((targetIndex) => {
    const targetStep = tutorialSteps[targetIndex];
    if (!targetStep) return;
    setTutorialStepIndex(targetIndex);
    window.requestAnimationFrame(() => {
      scrollToTutorialTarget(targetStep);
    });
  }, [scrollToTutorialTarget, tutorialSteps]);

  const handleTutorialCallback = useCallback((data) => {
    const { action, index, status, type } = data;
    const isLastStep = index >= tutorialSteps.length - 1;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      finishTutorialRun();
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      if (action !== ACTIONS.PREV && isLastStep) {
        finishTutorialRun();
        return;
      }

      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      if (nextIndex >= 0 && nextIndex < tutorialSteps.length) {
        setTutorialStepIndex(nextIndex);
        window.requestAnimationFrame(() => {
          scrollToTutorialTarget(tutorialSteps[nextIndex]);
        });
        speakTutorialStep(tutorialSteps[nextIndex]);
      }
    }
  }, [finishTutorialRun, scrollToTutorialTarget, speakTutorialStep, tutorialSteps]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-100 px-4">
        <div className="rounded-[28px] border border-slate-200 bg-white px-8 py-10 text-center shadow-sm">
          <Icon
            path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992"
            className="mx-auto h-12 w-12 animate-spin text-blue-500"
          />
          <p className="mt-4 text-sm font-medium text-slate-600">Cargando gastos de flota…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Joyride
        key={`fleetExpenses-${tutorialSessionKey}`}
        run={tutorialRun}
        stepIndex={tutorialStepIndex}
        steps={tutorialSteps}
        callback={handleTutorialCallback}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        disableScrolling={false}
        disableOverlayClose
        spotlightClicks={false}
        locale={{ back: 'Atrás', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', skip: 'Omitir' }}
        styles={{
          options: {
            arrowColor: '#020617',
            backgroundColor: '#020617',
            overlayColor: 'rgba(2,6,23,0.34)',
            primaryColor: '#22d3ee',
            textColor: '#e2e8f0',
            width: 420,
            zIndex: 2100,
          },
          buttonNext: {
            backgroundColor: '#22d3ee',
            color: '#082f49',
            fontWeight: 700,
            borderRadius: '14px',
            boxShadow: '0 10px 24px rgba(34, 211, 238, 0.28)',
          },
          buttonBack: { color: '#cbd5e1' },
          buttonSkip: { color: '#94a3b8' },
          tooltip: {
            borderRadius: 24,
            boxShadow: '0 28px 80px rgba(2, 6, 23, 0.52)',
            border: '1px solid rgba(34, 211, 238, 0.18)',
            background: 'linear-gradient(145deg, rgba(2,6,23,0.98), rgba(15,23,42,0.98))',
          },
          tooltipTitle: { color: '#f8fafc', fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.02em' },
          tooltipContent: { color: '#cbd5e1', fontSize: '0.93rem', lineHeight: 1.6 },
          spotlight: {
            borderRadius: 28,
            boxShadow: '0 0 0 2px rgba(34, 211, 238, 0.5), 0 0 38px rgba(34, 211, 238, 0.18)',
          },
        }}
      />

      {tutorialRun ? (
        <div
          ref={tutorialShortcutPanelRef}
          className={`fixed z-[2300] hidden w-[18rem] flex-col gap-2 transition-all duration-300 md:flex ${
            tutorialShortcutDock === 'top-left'
              ? 'left-5 top-24'
              : tutorialShortcutDock === 'bottom-right'
                ? 'bottom-5 right-5'
                : tutorialShortcutDock === 'bottom-left'
                  ? 'bottom-5 left-5'
                  : 'right-5 top-24'
          }`}
        >
          <div className="inline-flex items-center justify-between gap-3 rounded-2xl border border-cyan-300/40 bg-slate-950/90 px-4 py-3 text-cyan-100 shadow-[0_18px_40px_rgba(2,6,23,0.42)] backdrop-blur-xl">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-2.5 w-2.5 rounded-full bg-cyan-300 animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-[0.16em]">Guía del recorrido</span>
            </div>
            <button
              type="button"
              onClick={() => setTutorialShortcutOpen((current) => !current)}
              className="inline-flex items-center gap-2 rounded-xl border border-cyan-300/30 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-cyan-100 transition hover:bg-cyan-300/10"
            >
              {tutorialShortcutOpen ? 'Ocultar pasos' : 'Ver pasos'}
            </button>
          </div>
          <div className={`overflow-hidden rounded-2xl border border-cyan-300/20 bg-slate-950/88 shadow-[0_18px_40px_rgba(2,6,23,0.42)] backdrop-blur-xl transition-all duration-300 ${tutorialShortcutOpen ? 'max-h-[70vh] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="border-b border-slate-800/80 px-4 py-3">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-200">Tutorial de gastos de flota</p>
              <p className="mt-1 text-xs text-slate-400">Podés saltar directo a cualquier parte del recorrido.</p>
            </div>
            <div className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto p-2">
              {tutorialSteps.map((step, index) => (
                <button
                  key={`fleetExpenses-${step.title}-${index}`}
                  type="button"
                  onClick={() => jumpToTutorialStep(index)}
                  className={`rounded-xl border px-3 py-2 text-left text-sm transition ${
                    index === tutorialStepIndex
                      ? 'border-cyan-300/60 bg-cyan-400/18 text-cyan-50 shadow-[0_0_0_1px_rgba(103,232,249,0.22),0_10px_30px_rgba(8,145,178,0.18)]'
                      : 'border-transparent text-slate-300 hover:border-slate-700 hover:bg-slate-800/80 hover:text-white'
                  }`}
                >
                  <span className={`block text-[10px] font-bold uppercase tracking-[0.16em] ${index === tutorialStepIndex ? 'text-cyan-200' : 'text-slate-500'}`}>Paso {index + 1}</span>
                  <span className="mt-0.5 block font-semibold">{step.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div id="fleet-expenses-module" className="h-full overflow-y-auto bg-slate-100 px-4 py-4 md:px-6 md:py-6">
      <section id="fleet-expenses-header" className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.10),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.08),transparent_28%)]" />
        <div className="relative flex flex-col gap-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={onBackToMenu}
                className="inline-flex min-w-[10.5rem] items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-[linear-gradient(180deg,#1e293b,#0f172a)] px-4 py-2 text-sm font-bold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-[1px] hover:bg-slate-800"
              >
                <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="h-4 w-4" />
                <span>Volver</span>
              </button>
              <span className="text-slate-300">|</span>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500">Centro operativo</p>
              <h2 className="text-xl font-bold tracking-[-0.03em] text-slate-900 md:text-2xl">Gastos de flota</h2>
              <span className="text-slate-300">|</span>
              <p className="text-sm font-medium text-slate-500">movimientos, categorías y seguimiento</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              <SectionBadge tone="cyan">
                <Icon path="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h6M9 11.25h6M9 15.75h6" className="h-4 w-4" />
                {resolvedCompanyName}
              </SectionBadge>
              <button
                type="button"
                onClick={onStartTutorial || (tutorialRun ? finishTutorialRun : startTutorial)}
                className={`inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-800 transition-all hover:-translate-y-0.5 hover:bg-cyan-100 ${effectiveTutorialRunning ? 'ring-2 ring-cyan-200' : ''} ${effectiveTutorialSeen ? '' : 'animate-pulse shadow-[0_0_0_6px_rgba(34,211,238,0.16),0_0_24px_rgba(34,211,238,0.22)]'}`}
              >
                <Icon path="M12 18v.01M8.25 9.75a3.75 3.75 0 117.5 0c0 1.53-.714 2.29-1.5 3-.78.705-1.5 1.34-1.5 2.49v.51" className="h-4 w-4" />
                {effectiveTutorialRunning ? 'Tutorial activo' : 'Tutorial guiado'}
              </button>
              <SectionBadge tone="violet">
                <Icon path="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h6m3-3.75l-3.75-3.75A1.125 1.125 0 015.25 6H3.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25h1.5a1.125 1.125 0 01.83.375l3.75 3.75a1.125 1.125 0 010 1.59l-3.75 3.75a1.125 1.125 0 01-.83.375H3.75a2.25 2.25 0 01-2.25-2.25v-1.5a2.25 2.25 0 012.25-2.25h1.5A1.125 1.125 0 005.25 12z" className="h-4 w-4" />
                {categories.length} categorías
              </SectionBadge>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" onMouseEnter={() => setGiaSection('Resumen de flota')}>
        <StatCard
          title="Total filtrado"
          value={`$ ${formatCurrency(summary.totalFiltered)}`}
          helper={filter.month || filter.type || filter.transportId ? 'Resultado según filtros activos' : 'Lectura general visible'}
          accent="blue"
          iconPath="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h6m3-3.75l-3.75-3.75A1.125 1.125 0 015.25 6H3.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25h1.5a1.125 1.125 0 01.83.375l3.75 3.75a1.125 1.125 0 010 1.59l-3.75 3.75a1.125 1.125 0 01-.83.375H3.75a2.25 2.25 0 01-2.25-2.25v-1.5a2.25 2.25 0 012.25-2.25h1.5A1.125 1.125 0 005.25 12z"
        />
        <StatCard
          title="Mes actual"
          value={`$ ${formatCurrency(summary.totalCurrentMonth)}`}
          helper={currentMonth}
          accent="emerald"
          iconPath="M8.25 6.75h7.5M8.25 10.5h7.5M8.25 14.25h4.5M6.75 3.75h10.5A2.25 2.25 0 0119.5 6v12A2.25 2.25 0 0117.25 20.25H6.75A2.25 2.25 0 014.5 18V6a2.25 2.25 0 012.25-2.25z"
        />
        <StatCard
          title="Movimientos"
          value={summary.expenseCount}
          helper="Registros visibles en pantalla"
          accent="violet"
          iconPath="M3 13.125C3 12.503 3.503 12 4.125 12h2.25c.622 0 1.125.503 1.125 1.125v6.75C7.5 20.497 6.997 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.622.503-1.125 1.125-1.125h2.25c.622 0 1.125.503 1.125 1.125v11.25c0 .622-.503 1.125-1.125 1.125h-2.25A1.125 1.125 0 019.75 19.875V8.625zM16.5 4.125C16.5 3.503 17.003 3 17.625 3h2.25C20.497 3 21 3.503 21 4.125v15.75c0 .622-.503 1.125-1.125 1.125h-2.25A1.125 1.125 0 0116.5 19.875V4.125z"
        />
        <StatCard
          title="Categoría líder"
          value={summary.topCategory}
          helper={`Promedio: $ ${formatCurrency(summary.averageExpense)}`}
          accent="slate"
          iconPath="M11.25 3.75h1.5A2.25 2.25 0 0115 6v.75h1.5A2.25 2.25 0 0118.75 9v7.5A2.25 2.25 0 0116.5 18.75h-9A2.25 2.25 0 015.25 16.5V9A2.25 2.25 0 017.5 6.75H9V6a2.25 2.25 0 012.25-2.25z"
        />
      </section>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
        <section className="xl:col-span-4 space-y-6">
          <div id="fleet-expenses-form" className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm" onMouseEnter={() => setGiaSection('Registrar gasto')}>
            <PanelHeader
              eyebrow="Carga operativa"
              title="Registrar gasto"
              description="Cargá movimientos de manera rápida y dejá trazabilidad sobre la operación diaria."
            />

            <form onSubmit={handleAddExpense} className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                <div>
                  <FieldLabel>Fecha</FieldLabel>
                  <input type="date" name="date" value={formData.date} onChange={handleInputChange} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100" />
                </div>
                <div id="fleet-expenses-category-field">
                  <FieldLabel>Categoría</FieldLabel>
                  <select name="type" value={formData.type} onChange={handleInputChange} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm capitalize text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100">
                    <option value="">Seleccionar categoría…</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}{cat.scopeLabel ? ` · ${cat.scopeLabel}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div id="fleet-expenses-transport-field" className="md:col-span-2 xl:col-span-1 2xl:col-span-2">
                  <FieldLabel>Unidad operativa</FieldLabel>
                  <select name="transportId" value={formData.transportId} onChange={handleInputChange} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100">
                    <option value="">Seleccionar unidad…</option>
                    {transports.map((transport) => (
                      <option key={transport.id} value={transport.id}>{buildTransportDisplayName(transport)}{buildTransportMeta(transport) ? ` · ${buildTransportMeta(transport)}` : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedCategory ? (
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{selectedCategory.name}</p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-500">
                        {selectedCategory.description || 'Categoría activa para este registro.'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <SectionBadge tone={selectedCategory.impactDriver ? 'amber' : 'blue'}>{selectedCategory.scopeLabel || 'Transporte'}</SectionBadge>
                      {selectedCategory.isFixed ? <SectionBadge tone="slate">Base de empresa</SectionBadge> : null}
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedCategory?.impactDriver ? (
                <div id="fleet-expenses-driver-field">
                  <FieldLabel>Chofer involucrado</FieldLabel>
                  <select name="driverId" value={formData.driverId} onChange={handleInputChange} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100">
                    <option value="">Seleccionar chofer…</option>
                    {drivers.map((driver) => (
                      <option key={driver.id} value={driver.id}>
                        {buildDriverDisplayName(driver)}{buildDriverMeta(driver) ? ` · ${buildDriverMeta(driver)}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    Multas e incidentes dejan registro sobre la unidad y también sobre el chofer involucrado.
                  </p>
                </div>
              ) : null}

              {selectedTransport ? (
                <div id="fleet-expenses-transport-preview" className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-2xl border"
                      style={{
                        backgroundColor: selectedTransport.identitySoftColor || resolveTransportIdentity(selectedTransport).identitySoftColor,
                        borderColor: selectedTransport.identityColor || resolveTransportIdentity(selectedTransport).identityColor,
                        color: selectedTransport.identityColor || resolveTransportIdentity(selectedTransport).identityColor,
                      }}
                    >
                      <Icon path={selectedTransport.transportIconPath || getTransportVisualConfig(selectedTransport.transportType || 'camioneta').path} className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{buildTransportDisplayName(selectedTransport)}</p>
                      <p className="text-xs text-slate-500">{buildTransportMeta(selectedTransport) || 'Unidad lista para imputar gasto'}</p>
                      {(selectedTransport.currentDriverName || selectedTransport.driverName) ? (
                        <p className="mt-1 text-[11px] font-medium text-slate-500">Chofer actual: {selectedTransport.currentDriverName || selectedTransport.driverName}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              <div>
                <FieldLabel>Monto</FieldLabel>
                <input type="number" name="amount" value={formData.amount} onChange={handleInputChange} placeholder="Monto ($)" className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100" />
              </div>

              <div>
                <FieldLabel>Descripción</FieldLabel>
                <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Detalle del movimiento" rows="3" className="min-h-[96px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100" />
              </div>

              <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700">
                <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-5 w-5" />
                Añadir gasto
              </button>
            </form>
          </div>

          <div id="fleet-expenses-categories" className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm" onMouseEnter={() => setGiaSection('Categorías de gasto')}>
            <PanelHeader
              eyebrow="Catálogo"
              title="Categorías"
              description="La empresa nace con categorías base protegidas y además podés sumar otras personalizadas para tu operación."
              extra={<SectionBadge tone="amber">{categories.length} activas</SectionBadge>}
            />

            <div className="mb-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-500">
              Categorías base activas: <span className="font-semibold text-slate-700">{DEFAULT_FLEET_EXPENSE_CATEGORIES.length}</span>.
              Las categorías creadas por la empresa suman nuevas lecturas sin afectar la independencia lógica de otras compañías.
            </div>

            <form onSubmit={handleAddCategory} className="mb-4 grid grid-cols-[1fr_auto_auto] gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Nueva categoría"
                className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              />
              <input type="color" value={newCategoryColor} onChange={(e) => setNewCategoryColor(e.target.value)} className="h-[52px] w-[52px] rounded-2xl border border-slate-200 bg-white p-1" />
              <button type="submit" className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700">
                <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-5 w-5" />
              </button>
            </form>

            {categories.length === 0 ? (
              <EmptyState
                title="Todavía no hay categorías"
                description="Creá la primera categoría para empezar a ordenar los movimientos de flota en este espacio."
              />
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: cat.color }} />
                        <span className="truncate text-sm font-semibold text-slate-700">{cat.name}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <SectionBadge tone={cat.impactDriver ? 'amber' : 'blue'}>{cat.scopeLabel || 'Transporte'}</SectionBadge>
                        {cat.isFixed ? <SectionBadge tone="slate">Base</SectionBadge> : <SectionBadge tone="emerald">Personalizada</SectionBadge>}
                      </div>
                    </div>
                    {cat.isFixed || cat.isSystem ? (
                      <span className="inline-flex h-9 items-center rounded-full border border-slate-200 px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Fija</span>
                    ) : (
                      <button onClick={() => handleDeleteCategory(cat.id)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-rose-500 transition hover:bg-rose-100 hover:text-rose-700">
                        <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="xl:col-span-8 space-y-6">
          <div id="fleet-expenses-history" className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm" onMouseEnter={() => setGiaSection('Historial de gastos')}>
            <PanelHeader
              eyebrow="Seguimiento"
              title="Historial de gastos"
              description="Filtrá movimientos y detectá rápidamente cómo se distribuye el costo operativo."
              extra={
                filteredExpenses.length > 0 ? (
                  <SectionBadge tone="blue">{filteredExpenses.length} movimientos visibles</SectionBadge>
                ) : null
              }
            />

            <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
              <select
                value={filter.month}
                onChange={(e) => setFilter((prev) => ({ ...prev, month: e.target.value }))}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">Todos los meses</option>
                {availableMonths.map((month) => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>

              <select
                value={filter.type}
                onChange={(e) => setFilter((prev) => ({ ...prev, type: e.target.value }))}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>

              <select
                value={filter.transportId}
                onChange={(e) => setFilter((prev) => ({ ...prev, transportId: e.target.value }))}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">Todas las unidades</option>
                {transports.map((transport) => (
                  <option key={transport.id} value={transport.id}>{buildTransportDisplayName(transport)}</option>
                ))}
              </select>

              {(filter.month || filter.type || filter.transportId) ? (
                <button
                  type="button"
                  onClick={() => setFilter({ month: '', type: '', transportId: '' })}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                  Limpiar filtros
                </button>
              ) : null}
            </div>

            {filteredExpenses.length === 0 ? (
              <EmptyState
                title="No hay movimientos para esta vista"
                description="Probá cambiar los filtros o cargá un nuevo gasto para empezar a ver actividad en este panel."
              />
            ) : (
              <div className="overflow-hidden rounded-[24px] border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Categoría</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Unidad</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Monto</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Descripción</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {filteredExpenses.map((exp) => {
                        const category = categories.find((c) => c.id === exp.type);
                        const expenseIdentity = resolveTransportIdentity(exp);
                        const expenseVisual = getTransportVisualConfig(exp.transportType || 'camioneta');
                        const expenseTransportName = exp.transportName || 'Unidad sin identificar';
                        const expenseTransportMeta = exp.transportMeta || [exp.transportLabel, exp.transportModel, exp.transportPatent].filter(Boolean).join(' · ');

                        return (
                          <tr key={exp.id} className="transition hover:bg-slate-50/80">
                            <td className="px-4 py-4 text-sm font-medium text-slate-700">{new Date(`${exp.date}T00:00:00`).toLocaleDateString('es-AR')}</td>
                            <td className="px-4 py-4 text-sm text-slate-800">
                              {category ? (
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5">
                                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: category.color }} />
                                  <span className="font-semibold">{category.name}</span>
                                  {(exp.impactDriver || category?.impactDriver) ? <span className="text-[11px] font-semibold text-amber-600">Chofer + unidad</span> : null}
                                </div>
                              ) : (
                                <span className="text-slate-400">Sin categoría</span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-800">
                              <div className="flex items-center gap-3">
                                <div
                                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border"
                                  style={{
                                    backgroundColor: exp.identitySoftColor || expenseIdentity.identitySoftColor,
                                    borderColor: exp.identityColor || expenseIdentity.identityColor,
                                    color: exp.identityColor || expenseIdentity.identityColor,
                                  }}
                                >
                                  <Icon path={exp.transportIconPath || expenseVisual.path} className="h-4.5 w-4.5" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-900">{expenseTransportName}</p>
                                  <p className="text-xs text-slate-500">{expenseTransportMeta || 'Sin referencia de unidad'}</p>
                                  {exp.driverName ? <p className="mt-1 text-[11px] font-medium text-slate-500">Chofer: {exp.driverName}{exp.driverMeta ? ` · ${exp.driverMeta}` : ''}</p> : null}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-right text-sm font-bold text-slate-900">$ {formatCurrency(exp.amount)}</td>
                            <td className="px-4 py-4 text-sm text-slate-500">{exp.description || 'Sin detalle'}</td>
                            <td className="px-4 py-4 text-center">
                              <button
                                onClick={() => handleDeleteExpense(exp.id)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-rose-500 transition hover:bg-rose-100 hover:text-rose-700"
                              >
                                <Icon path="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div id="fleet-expenses-chart" className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm" onMouseEnter={() => setGiaSection('Distribución por categoría')}>
            <PanelHeader
              eyebrow="Lectura visual"
              title="Distribución por categoría"
              description="Compará rápidamente el peso económico de cada categoría según la vista filtrada."
            />

            {statsData.categoryChartData.length === 0 ? (
              <EmptyState
                title="Todavía no hay datos para graficar"
                description="Cuando registres movimientos en esta vista, vas a poder compararlos por categoría desde este panel."
              />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={statsData.categoryChartData} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tickFormatter={(value) => `$${formatCurrency(value)}`} stroke="#64748b" fontSize={12} />
                  <YAxis type="category" dataKey="name" width={120} stroke="#64748b" fontSize={12} />
                  <Tooltip formatter={(value) => `$ ${formatCurrency(value)}`} cursor={{ fill: 'rgba(148,163,184,0.08)' }} />
                  <Bar dataKey="value" radius={[0, 10, 10, 0]} barSize={22}>
                    {statsData.categoryChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>
      </div>
      </div>
    </>
  );
};

export default FleetExpensesApp;
